import { useState } from "react";
import { trpc, toastTrpcError } from "@/lib/trpc";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { LinearIcon } from "@/components/icons";
import { LinearConnectDialog } from "@/components/settings/linear-connect-dialog";
import { toast } from "sonner";
import {
  AlertTriangle,
  CheckCircle2,
  ExternalLink,
  Key,
  Trash2,
  Users,
} from "lucide-react";

interface LinearTeamsListProps {
  connectionId: string;
}

function LinearTeamsList({ connectionId }: LinearTeamsListProps) {
  const {
    data: teams,
    isLoading,
    error,
  } = trpc.integration.linear.listTeams.useQuery(
    { connectionId },
    { retry: 1 },
  );

  if (isLoading) {
    return <Skeleton className="h-6 w-48 rounded" />;
  }

  if (error) {
    return (
      <span className="text-xs text-muted-foreground">
        Unable to load teams ({error.message})
      </span>
    );
  }

  if (!teams || teams.length === 0) {
    return (
      <span className="text-xs text-muted-foreground">No teams found</span>
    );
  }

  return (
    <div className="flex flex-wrap items-center gap-1.5 pt-1">
      <span className="text-xs text-muted-foreground flex items-center gap-1 mr-1">
        <Users className="h-3 w-3" />
        Teams:
      </span>
      {teams.map((t) => (
        <Badge
          key={t.id}
          variant="secondary"
          className="rounded-sm text-xs font-mono"
        >
          {t.name} ({t.key})
        </Badge>
      ))}
    </div>
  );
}

export function LinearIntegration() {
  const [isConnectOpen, setIsConnectOpen] = useState(false);

  const {
    data: connections,
    isLoading,
    refetch,
  } = trpc.integration.listConnections.useQuery({ provider: "linear" });

  const disconnectMutation = trpc.integration.linear.disconnect.useMutation({
    onSuccess: () => {
      toast.success("Linear connection removed");
      refetch();
    },
    onError: (err) => {
      toastTrpcError(err, "Failed to disconnect Linear");
    },
  });

  const linearConnections =
    connections?.filter((c) => c.provider === "linear") ?? [];
  const hasConnections = linearConnections.length > 0;

  return (
    <>
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-muted text-foreground">
                <LinearIcon className="h-5 w-5" />
              </div>
              <div>
                <CardTitle className="text-md">Linear Integration</CardTitle>
                <CardDescription>
                  Connect Linear using a personal API key to sync issues,
                  workflow states, and automated walkthrough results.
                </CardDescription>
              </div>
            </div>
            <Button
              size="sm"
              onClick={() => setIsConnectOpen(true)}
              className="gap-2"
            >
              <Key className="h-3.5 w-3.5" />
              {hasConnections ? "Add Connection" : "Connect Linear"}
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {isLoading ? (
            <div className="space-y-3">
              <Skeleton className="h-16 w-full rounded-lg" />
              <Skeleton className="h-16 w-full rounded-lg" />
            </div>
          ) : !hasConnections ? (
            <div className="rounded-lg bg-muted/30 p-6 text-center text-sm text-muted-foreground">
              <p>No Linear connection configured yet.</p>
              <p className="mt-1 text-xs">
                Generate an API key in Linear Settings &gt; Account &gt; API
                &gt; Personal API keys.
              </p>
            </div>
          ) : (
            <div className="divide-y divide-border/60 rounded-lg bg-muted/20">
              {linearConnections.map((conn) => {
                const isActive = conn.status === "active";
                const isNeedsReauth = conn.status === "needs_reauth";

                return (
                  <div
                    key={conn.id}
                    className="flex flex-col gap-3 p-4 sm:flex-row sm:items-center sm:justify-between"
                  >
                    <div className="space-y-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-sm">
                          {conn.displayName || "Linear"}
                        </span>
                        {isActive && (
                          <Badge
                            variant="outline"
                            className="rounded-sm gap-1 border-emerald-500/30 bg-emerald-500/10 text-emerald-600 text-xs"
                          >
                            <CheckCircle2 className="h-3 w-3" />
                            Active
                          </Badge>
                        )}
                        {isNeedsReauth && (
                          <Badge
                            variant="destructive"
                            className="rounded-sm gap-1 text-xs"
                          >
                            <AlertTriangle className="h-3 w-3" />
                            Re-auth Required
                          </Badge>
                        )}
                        {!isActive && !isNeedsReauth && (
                          <Badge
                            variant="secondary"
                            className="rounded-sm capitalize text-xs"
                          >
                            {conn.status}
                          </Badge>
                        )}
                      </div>

                      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground">
                        <a
                          href="https://linear.app"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-1 hover:underline hover:text-foreground"
                        >
                          <span>linear.app</span>
                          <ExternalLink className="h-3 w-3" />
                        </a>
                        {conn.webhookRouteId && (
                          <span className="font-mono text-xs">
                            Route: {conn.webhookRouteId}
                          </span>
                        )}
                      </div>

                      {isActive && <LinearTeamsList connectionId={conn.id} />}
                    </div>

                    <div className="flex items-center gap-2 shrink-0">
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-destructive hover:text-destructive hover:bg-destructive/10"
                        onClick={() =>
                          disconnectMutation.mutate({ connectionId: conn.id })
                        }
                        disabled={disconnectMutation.isPending}
                      >
                        <Trash2 className="h-4 w-4 mr-1" />
                        Disconnect
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      <LinearConnectDialog
        open={isConnectOpen}
        onOpenChange={setIsConnectOpen}
        onConnected={refetch}
      />
    </>
  );
}
